import type { DbSession, DbSnapshot, DbWatchStats } from '@apm/contracts';
import { routeHref } from '../router';
import { DB_SESSION_LABEL, TIP } from '../terms';
import { formatClock, formatDuration } from '../time';
import { Cell, type Column, EmptyState, Row, Table } from '../ui';

/**
 * DB 구획 (9회차 결정 4).
 *
 * 진행 중 요청 목록 아래에 붙는다. 앱 쪽에서 "DB 3.2초"로만 보이던 것이
 * 여기서는 "무엇을 기다리는 중인지"로 보인다.
 *
 * 세션과 요청은 SQL 앞 주석에 실린 요청 번호로 잇는다. 지금 도는 요청 중에 같은 번호가 있으면
 * 그 경로를 보여 주고(눌러서 상세로), 없으면 번호 앞 8자리만 보여 준다.
 */

/** 그 세션이 무엇 때문에 눈에 띄는지. 색은 이 순서로 센 것을 따른다 */
type Level = 'blocked' | 'blocker' | 'waiting' | 'idle-tx' | 'normal';

const ROW_TONE = {
  blocked: 'error',
  blocker: 'fail',
  waiting: 'fail',
  'idle-tx': 'muted',
  normal: 'ink',
} as const;

export function sessionLevel(session: DbSession, blockers: ReadonlySet<number>): Level {
  if (session.blockedBy !== undefined && session.blockedBy.length > 0) return 'blocked';
  if (blockers.has(session.pid)) return 'blocker';
  if (session.state.startsWith('idle in transaction')) return 'idle-tx';
  if (session.waitEventType !== undefined) return 'waiting';
  return 'normal';
}

/** 남을 막고 있는 pid 모음. 막힌 세션의 `blockedBy`를 모으면 된다 */
export function blockerPids(sessions: readonly DbSession[]): Set<number> {
  const out = new Set<number>();
  for (const session of sessions) {
    for (const pid of session.blockedBy ?? []) out.add(pid);
  }
  return out;
}

/** 세션의 경과. 도는 중이면 문장 경과, 트랜잭션만 열려 있으면 트랜잭션 경과 */
export function sessionAgeMs(session: DbSession): number {
  if (session.state === 'active') return session.queryAgeMs ?? 0;
  return session.xactAgeMs ?? session.stateAgeMs ?? 0;
}

const COLUMNS: readonly Column[] = [
  { label: 'pid', width: 'w-24' },
  { label: '상태', width: 'w-40' },
  { label: '대기 이벤트', width: 'w-44' },
  { label: '경과', width: 'w-24' },
  { label: 'SQL' },
  { label: '요청', width: 'w-60', title: TIP.dbTraceComment },
];

export function DbPanel({
  db,
  entries,
  watcher,
  error,
}: {
  db?: DbSnapshot;
  watcher?: Pick<DbWatchStats, 'enabled' | 'connected'>;
  error?: string;
  /** 지금 도는 요청의 요청 번호 → 경로 */
  entries: ReadonlyMap<string, string>;
}) {
  if (error !== undefined) {
    return <EmptyState title="DB 감시 상태를 확인하지 못했다" hint={error} />;
  }
  if (watcher === undefined) {
    return (
      <EmptyState title="DB 감시 상태를 확인 중이다" hint="감시 설정과 연결 상태를 읽고 있다" />
    );
  }
  if (!watcher.enabled) {
    return (
      <EmptyState
        title="DB 감시가 설정되지 않았다"
        hint="DB 세션 감시는 선택 기능이다. 앱 요청과 SQL 수집은 별도로 동작한다"
      />
    );
  }
  if (!watcher.connected) {
    return (
      <EmptyState
        title="DB 감시 연결이 끊겼다"
        hint="감시는 설정되어 있지만 대상 DB에 연결하지 못하고 있다. 접속 상태를 확인할 것"
      />
    );
  }
  if (db === undefined) {
    return (
      <EmptyState
        title="DB 감시 데이터를 기다리고 있다"
        hint="DB에 연결되었지만 아직 세션 정보를 받지 못했다"
      />
    );
  }

  const blockers = blockerPids(db.sessions);
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="활성" value={`${db.active}건`} />
        <Stat
          label="유휴 트랜잭션"
          value={`${db.idleInTx}건`}
          level={db.idleInTx > 0 ? 'warn' : 'normal'}
          warnLabel="확인 필요"
        />
        <Stat
          label="대기"
          value={`${db.waiting}건`}
          level={db.waiting > 0 ? 'warn' : 'normal'}
          warnLabel="있음"
        />
        <Stat
          label="막힘"
          value={`${db.blocked}건`}
          level={db.blocked > 0 ? 'danger' : 'normal'}
          warnLabel="있음"
        />
        <Stat label="최장 쿼리" value={formatDuration(db.longestQueryMs)} />
        <Stat
          label="요청 매칭률"
          value={`${Math.round(db.matchRate * 100)}%`}
          note={db.truncated > 0 ? `SQL 잘림 ${db.truncated}건` : undefined}
          level={db.matchRate < 0.9 ? 'warn' : 'normal'}
          warnLabel="낮음"
        />
      </dl>

      {db.sessions.length === 0 ? (
        <EmptyState
          title="지금 조회된 세션이 없다"
          hint="DB 감시는 연결되어 있고 이번 조회 결과는 0건이다"
        />
      ) : (
        <Table columns={COLUMNS} caption="DB 세션" minWidth="min-w-[960px]">
          {db.sessions.map((session) => (
            <SessionRow
              key={session.pid}
              session={session}
              level={sessionLevel(session, blockers)}
              entries={entries}
            />
          ))}
        </Table>
      )}

      <p className="break-words text-xs text-muted">
        {db.database} · 최장 트랜잭션 {formatDuration(db.longestXactMs)} · {formatClock(db.ts)} 기준
      </p>
    </div>
  );
}

function SessionRow({
  session,
  level,
  entries,
}: {
  session: DbSession;
  level: Level;
  entries: ReadonlyMap<string, string>;
}) {
  const label = DB_SESSION_LABEL[level];
  return (
    <Row>
      <Cell mono tone="muted" className="tabular">
        {session.pid}
      </Cell>
      <Cell tone={ROW_TONE[level]}>
        {label === '' ? session.state : label}
        {label !== '' && session.blockedBy !== undefined ? (
          <span className="ml-1 text-muted">pid {session.blockedBy.join(', ')}</span>
        ) : null}
      </Cell>
      <Cell tone="muted">
        {session.waitEventType === undefined
          ? '-'
          : `${session.waitEventType}: ${session.waitEvent ?? ''}`}
      </Cell>
      <Cell mono className="tabular">
        {formatDuration(sessionAgeMs(session))}
      </Cell>
      <Cell mono title={session.queryHead} className="max-w-[420px] truncate">
        {session.queryHead === '' ? '-' : session.queryHead}
        {session.truncated ? <span className="ml-2 text-fail">잘림</span> : null}
      </Cell>
      <Cell>
        <RequestLink traceId={session.traceId} entries={entries} appName={session.appName} />
      </Cell>
    </Row>
  );
}

function RequestLink({
  traceId,
  entries,
  appName,
}: {
  traceId?: string;
  entries: ReadonlyMap<string, string>;
  appName?: string;
}) {
  if (traceId === undefined) {
    return (
      <span className="block max-w-[220px] truncate text-muted" title={appName}>
        {appName ?? '요청 정보 없음'}
      </span>
    );
  }
  const entry = entries.get(traceId);
  return (
    <a
      href={routeHref({ name: 'detail', traceId })}
      title={traceId}
      className="block max-w-[220px] truncate font-mono text-link no-underline hover:underline"
    >
      {entry ?? traceId.slice(0, 8)}
    </a>
  );
}

/** 숫자 타일. `ui/Field`의 것과 같은 모양이지만 `<dl>` 안이라 여기서 감싼다 */
function Stat({
  label,
  value,
  note,
  level = 'normal',
  warnLabel,
}: {
  label: string;
  value: string;
  note?: string;
  level?: 'normal' | 'warn' | 'danger';
  warnLabel?: string;
}) {
  const tone = level === 'danger' ? 'text-error' : level === 'warn' ? 'text-fail' : 'text-ink';
  return (
    <div className="rounded-xl border border-hair bg-panel px-4 py-3">
      <dt className="whitespace-nowrap text-xs text-muted">{label}</dt>
      <dd className={`tabular mt-1 text-lg font-semibold ${tone}`}>
        {value}
        {level !== 'normal' && warnLabel ? (
          <span className="ml-1.5 text-xs font-normal">{warnLabel}</span>
        ) : null}
      </dd>
      {note ? <dd className="mt-0.5 break-words text-[11px] text-muted">{note}</dd> : null}
    </div>
  );
}
